// ─── Palette catalog ─────────────────────────────────────────────
// Each palette must declare: primary, primaryLight, accent, accentHover,
// background, surface, text. Optional: highlight, highlightHover, secondary,
// secondaryHover, sectionTint, brandSurface, onBrandSurface.
//
// highlight       = third chromatic brand color (warm/cool contrast). Auto-derived
//                   from primary/accent when omitted. Random palettes always set it.
// highlightHover  = hover state for highlight; defaults to a slightly darker mix.
// secondary       = dark text-adjacent tone on most palettes. Lime* keeps yellow
//                   here as legacy outlined-button support.
//
// brandSurface = navbar / footer / drawer background. Defaults to primary.
// onBrandSurface = text/icons on brandSurface. Defaults to "#FFFFFF".
//   → Override these when primary is too LIGHT for white text.
// sectionTint  = featured-section background. Falls back to a soft
//                95%-white mix of primary when not set.
//
// Resolution order, jitter, and random-palette injection live in
// tokens.js. This file is just the catalog.
// ─────────────────────────────────────────────────────────────────

export const palettes = {
  mintPlum: {
    primary: "#6D457D",
    primaryLight: "#9F7AAE",
    accent: "#92D7A8",
    accentHover: "#6DC98A",
    highlight: "#C2AB70",
    highlightHover: "#B69A53",
    background: "#F4EFF6",
    surface: "#FFFFFF",
    sectionTint: "#ECE2F1",
    secondary: "#2D2032",
    text: "#2D2032",
    brandSurface: "#563264",
    onBrandSurface: "#E7DFEA",
  },

  // PeachBlack — pure black chrome + warm peach CTA + plum highlights.
  // Stronger, more editorial / Vogue-food. The peach button glows on
  // black; plum sits as the secondary brand color.
  peachBlack: {
    primary: "#FFB07A", // peach as the lead brand color
    primaryLight: "#FFC499",
    accent: "#FFB07A", // peach CTA
    accentHover: "#F5905F",
    highlight: "#73E8A2",
    highlightHover: "#50E28B",
    background: "#FAF5F0", // warm parchment
    surface: "#FFFFFF",
    secondary: "#5D2A50", // deep plum accent
    text: "#1A1A1A",
    brandSurface: "#1A1A1A", // pure black chrome
    onBrandSurface: "#FFE5C9", // warm peach-cream text
  },
  pastelCream: {
    primary: "#BFA757",
    primaryLight: "#D7CBA5",
    accent: "#E5D9B1",
    accentHover: "#D9C68B",
    highlight: "#89D2C1",
    highlightHover: "#6BC7B1",
    background: "#F4F1E9",
    surface: "#FFFFFF",
    sectionTint: "#EEEADE",
    secondary: "#403A26",
    text: "#403A26",
    brandSurface: "#816F34",
    onBrandSurface: "#F0EEE8",
  },

  // Harbor — muted ocean teal-blue + warm peach accent on cool grey-blue.
  // Coastal / nautical / editorial. Deep navy chrome, peach CTA pops.
  harbor: {
    primary: "#2C7396", // ocean teal-blue — headings, links
    primaryLight: "#64A4C4", // softer sky-teal — hover, secondary
    accent: "#E8AA7D", // warm peach CTA
    accentHover: "#E08D52",
    highlight: "#D161AC",
    highlightHover: "#C8419B",
    background: "#F2F6F7", // cool grey-blue page bg
    surface: "#FFFFFF",
    sectionTint: "#F4ECE6", // warm sand section tint (warm/cool contrast)
    secondary: "#152228", // deep navy — body emphasis
    text: "#152228",
    brandSurface: "#153647", // deep navy chrome
    onBrandSurface: "#E4EDF1", // pale sky text on chrome
  },
  jewelGreen: {
    primary: "#4F8615",
    primaryLight: "#83CF32",
    accent: "#E3D26D",
    accentHover: "#DBC542",
    highlight: "#83CF32",
    highlightHover: "#6FAF28",
    background: "#F7F6F6",
    surface: "#FFFFFF",
    sectionTint: "#EBF2E3",
    secondary: "#1B2312",
    text: "#1B2312",
    brandSurface: "#213709",
    onBrandSurface: "#E9F1E1",
  },
  limeForestBlack: {
    primary: "#2A5C3F", // forest green — headings, links
    primaryLight: "#3F7A58", // hover states
    accent: "#ACEB74", // your lime — CTAs, highlights
    accentHover: "#92E547",
    highlight: "#E3D26D",
    highlightHover: "#DBC542",
    background: "#F6F8F5", // cool off-white with a green hint
    surface: "#FFFFFF",
    secondary: "#E3D26D", // jewel yellow — badges, secondary CTAs, highlights
    secondaryHover: "#DBC542",
    text: "#121814", // near-black (green undertone, softer than pure black)
    brandSurface: "#0F1210", // black chrome — nav, footer, drawer
    onBrandSurface: "#E8F2DC", // pale green-white on dark chrome
  },

  limeOnBlack: {
    primary: "#1E4D32", // deep pine
    primaryLight: "#2F6B4A",
    accent: "#ACEB74",
    accentHover: "#92E547",
    highlight: "#E3D26D",
    highlightHover: "#DBC542",
    background: "#FAFAFA", // neutral light page (lets green breathe)
    surface: "#FFFFFF",
    secondary: "#E3D26D",
    secondaryHover: "#DBC542",
    text: "#0A0A0A", // true black text
    brandSurface: "#000000", // pure black chrome
    onBrandSurface: "#ACEB74", // lime text/icons on nav — very bold
  },
};
